// pages/FormViewerPage/hooks/useFormViewer/effectsCore.ts — Fetch + validation-clear + platform parsing effects
import { useEffect } from 'react';
import { sanitizeInput } from '../../../../utils/sanitization';
import { isValidEmail, validateWhatsappNumber, fetchMessageTemplates, fetchBiteshipApiKey, MIN_ADDRESS_LENGTH } from '../../services/formViewerService';
import { captureAttributionFromUrl } from '../../../../services/attributionIds';
import type { FormViewerState } from './state';

// Label metode bayar prospek → kandidat key PaymentSettings (dipilih yg visible di form)
const PAY_FAMILIES: Record<string, string[]> = {
    cod: ['cod', 'codJawaBali', 'codLuarJawaBali'],
    transfer: ['bankTransfer'],
    qris: ['qris'],
    ewallet: ['duitku'],
};
function resolvePrefillPaymentKey(form: any, label: string): string | null {
    const l = (label || '').toLowerCase();
    const fam = l.includes('cod') ? PAY_FAMILIES.cod
        : l.includes('transfer') || l.includes('bank') ? PAY_FAMILIES.transfer
        : l.includes('qris') ? PAY_FAMILIES.qris
        : l.includes('wallet') || l.includes('dana') || l.includes('ovo') || l.includes('gopay') ? PAY_FAMILIES.ewallet
        : null;
    if (!fam) return null;
    const ps = form?.paymentSettings || {};
    return fam.find(k => ps[k] && ps[k].visible !== false) || null;
}

export function useFormViewerCoreEffects(s: FormViewerState) {
    const { form, setMessageTemplates, setForceShowPostalCode, addressData, setAddressData, setBiteshipApiKey, fieldErrors, setFieldErrors, customerData, setCustomerData, setActivePlatform, setUrlVariantParams, setQuantity, setSelectedPaymentKey, selectedOptions } = s;

    // Templates non-kritis (dipakai saat submit): idle agar tak berebut koneksi dgn query form.
    useEffect(() => {
        const run = () => { fetchMessageTemplates().then(setMessageTemplates); };
        if ('requestIdleCallback' in window) (window as any).requestIdleCallback(run, { timeout: 4000 }); else setTimeout(run, 2000);
    }, []);

    // Clear forceShowPostalCode on address change
    useEffect(() => { setForceShowPostalCode(false); }, [addressData.province]);

    // Biteship API key fetch
    useEffect(() => {
        fetchBiteshipApiKey(form).then(key => setBiteshipApiKey(key));
    }, [form?.biteshipSettings?.apiKey]);

    // JANGAN hapus pilihan kurir saat ganti COD↔Transfer (bikin ongkir hilang).
    // EverproShippingRates/index.tsx me-revalidasi diam-diam: objek tarif di-refresh
    // (signed_key segar) atau dibersihkan hanya kalau kurirnya tak ada di daftar baru.

    // Real-time validation clear
    useEffect(() => {
        if (Object.keys(fieldErrors).length === 0) return;
        const errorsToRemove: string[] = [];
        if (fieldErrors.name && customerData.name.trim().length >= 3) errorsToRemove.push('name');
        if (fieldErrors.email && customerData.email.trim() && isValidEmail(customerData.email)) errorsToRemove.push('email');
        if (fieldErrors.whatsapp) { const wc = validateWhatsappNumber(customerData.whatsapp); if (wc.isValid) errorsToRemove.push('whatsapp'); }
        if (fieldErrors.province && addressData.province?.trim()) errorsToRemove.push('province');
        if (fieldErrors.city && addressData.city?.trim()) errorsToRemove.push('city');
        if (fieldErrors.district && addressData.district?.trim()) errorsToRemove.push('district');
        if (fieldErrors.village && addressData.village?.trim()) errorsToRemove.push('village');
        if (fieldErrors.address) { const addr = (addressData.detailAddress || addressData.fullAddress || '').trim(); const minChars = Math.max(MIN_ADDRESS_LENGTH, form?.customerFields.address?.minCharacters || 0); if (addr.length >= minChars) errorsToRemove.push('address'); }
        for (const k of Object.keys(fieldErrors)) {
            if (k.startsWith('variant:') && String(selectedOptions[k.slice(8)] || '').trim()) errorsToRemove.push(k);
        }
        if (errorsToRemove.length > 0) setFieldErrors(prev => { const u = { ...prev }; errorsToRemove.forEach(k => delete u[k]); return u; });
    }, [customerData.name, customerData.email, customerData.whatsapp, addressData.province, addressData.city, addressData.district, addressData.village, addressData.detailAddress, addressData.fullAddress, form?.customerFields.address?.minCharacters, selectedOptions]);

    // Platform + variant URL parsing
    useEffect(() => {
        captureAttributionFromUrl();
        const mergedParams = new URLSearchParams();
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.forEach((v, k) => mergedParams.set(k, v));
        let platformParam = sanitizeInput(mergedParams.get('platform') || '') as any;
        let utmSource = sanitizeInput(mergedParams.get('utm_source') || '')?.toLowerCase();
        if (!utmSource) { if (mergedParams.has('fbclid')) utmSource = 'meta'; else if (mergedParams.has('gclid')) utmSource = 'google'; else if (mergedParams.has('ttclid')) utmSource = 'tiktok'; }
        if (!platformParam && utmSource) { if (['fb', 'facebook', 'ig', 'instagram', 'meta'].includes(utmSource)) platformParam = 'meta'; else if (['tiktok', 'tt'].includes(utmSource)) platformParam = 'tiktok'; else if (['google', 'gads'].includes(utmSource)) platformParam = 'google'; else if (['snack', 'snackvideo'].includes(utmSource)) platformParam = 'snack'; }
        if (platformParam && ['meta', 'tiktok', 'google', 'snack', 'openai'].includes(platformParam)) setActivePlatform(platformParam);
        else if (form?.assignedPlatform) setActivePlatform(form.assignedPlatform);
        else setActivePlatform(null);
        const prefilledName = sanitizeInput(mergedParams.get('name') || '');
        const prefilledPhone = sanitizeInput(mergedParams.get('phone') || '');
        if (prefilledName || prefilledPhone) setCustomerData(prev => ({ ...prev, name: prefilledName || prev.name, whatsapp: prefilledPhone || prev.whatsapp }));
        const variantParams: Record<string, string> = {};
        mergedParams.forEach((value, key) => { if (!['platform', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'gclid', 'ttclid', 'name', 'phone', 'qty', 'pay', 'addr', 'variant', 'lead'].includes(key)) variantParams[sanitizeInput(key)] = sanitizeInput(decodeURIComponent(value)); });
        // Prefill dari prospek CS: ?variant= (cocok ke SEMUA option yg valuenya match) + qty/pay/addr/lead
        const leadVariant = sanitizeInput(mergedParams.get('variant') || '');
        if (leadVariant) variantParams.__lead_variant__ = leadVariant;
        if (Object.keys(variantParams).length > 0) setUrlVariantParams(variantParams);
        const qtyParam = parseInt(mergedParams.get('qty') || '', 10);
        if (qtyParam >= 1 && qtyParam <= 99) setQuantity(qtyParam);
        const payParam = sanitizeInput(mergedParams.get('pay') || '');
        if (payParam && form) { const key = resolvePrefillPaymentKey(form, payParam); if (key) setSelectedPaymentKey(key as any); }
        const addrParam = sanitizeInput(mergedParams.get('addr') || '');
        if (addrParam) {
            setCustomerData(prev => ({ ...prev, address: addrParam }));
            setAddressData(prev => ({ ...prev, detailAddress: addrParam, fullAddress: addrParam }));
        }
        const leadParam = sanitizeInput(mergedParams.get('lead') || '');
        if (leadParam) { try { sessionStorage.setItem('leadOrderLink', leadParam); } catch { /* abaikan */ } }
    }, [form?.assignedPlatform]);
}
